import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CalendarDays, MapPin, ExternalLink, Quote, Flag } from "lucide-react";
import api from "@/lib/api";
import { RaceJoinButton, RaceJoiners, RacePlanButton } from "@/components/RaceJoin";

/**
 * Dashboard Welcome Board — the pack's upcoming races with a "Count me in" call.
 * Admin controls the list + rally pitch from Admin → Races.
 */
export default function WelcomeBoard({ firstName }) {
  const [races, setRaces] = useState([]);

  useEffect(() => {
    api.get("/races").then((r) => setRaces(r.data || [])).catch(() => {});
  }, []);

  const cutoff = Date.now() - 6 * 60 * 60 * 1000;
  const upcoming = races.filter((r) => new Date(r.date).getTime() >= cutoff).slice(0, 4);
  if (upcoming.length === 0) return null;

  const replace = (updated) => setRaces((rs) => rs.map((r) => (r.race_id === updated.race_id ? updated : r)));
  const mine = upcoming.filter((r) => r.joined).length;

  return (
    <section data-testid="welcome-board" className="mt-6 hairline border-zinc-800 bg-zinc-950 overflow-hidden relative">
      <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(ellipse_at_top_right,rgba(234,88,12,0.14),transparent_55%)]" aria-hidden="true" />
      <div className="relative px-6 sm:px-8 pt-7 pb-6 flex flex-col sm:flex-row sm:items-end justify-between gap-4 border-b border-zinc-800">
        <div>
          <p className="font-mono text-[10px] tracking-[0.3em] text-orange-500 uppercase flex items-center gap-2">
            <Flag size={11} /> WELCOME BOARD · THE PACK IS RACING
          </p>
          <h2 className="font-display text-3xl sm:text-4xl text-white mt-2">
            {firstName ? `${firstName}, ` : ""}come run these <span className="text-orange-500">with us.</span>
          </h2>
          <p className="text-sm text-zinc-400 mt-2 max-w-2xl">
            Races OSCR is travelling to next. Tap <span className="text-white">COUNT ME IN</span> so we can plan travel, bibs and
            the cheering squad together.
          </p>
        </div>
        <div className="flex items-center gap-5 shrink-0">
          <div className="text-right" data-testid="welcome-board-mine">
            <div className="font-mono text-3xl text-white leading-none">{mine}</div>
            <div className="font-mono text-[9px] tracking-[0.3em] text-zinc-500 uppercase mt-1">YOU&apos;RE IN</div>
          </div>
          <Link to="/races" data-testid="welcome-board-all-races" className="font-display text-sm tracking-wider text-orange-500 hover:text-orange-400 inline-flex items-center gap-1">
            ALL RACES <ArrowRight size={14} />
          </Link>
        </div>
      </div>

      <div className="relative divide-y divide-zinc-800">
        {upcoming.map((r, i) => (
          <RaceRow key={r.race_id} race={r} index={i} onChange={replace} />
        ))}
      </div>
    </section>
  );
}

function RaceRow({ race, index, onChange }) {
  const d = new Date(race.date);
  const days = Math.max(0, Math.ceil((d.getTime() - Date.now()) / 86400000));
  return (
    <article data-testid={`welcome-race-${race.race_id}`} className="px-6 sm:px-8 py-6 grid grid-cols-1 lg:grid-cols-12 gap-5 lg:gap-8 hover:bg-zinc-900/40 transition">
      {/* Date block */}
      <div className="lg:col-span-2 flex lg:flex-col items-center lg:items-start gap-4 lg:gap-0">
        <div className="hairline border-zinc-800 bg-zinc-900 px-4 py-3 text-center min-w-[88px]">
          <div className="font-mono text-[9px] tracking-[0.3em] text-orange-500 uppercase">{d.toLocaleString("en-IN", { month: "short" })}</div>
          <div className="font-display text-4xl text-white leading-none mt-1">{d.getDate()}</div>
          <div className="font-mono text-[9px] tracking-[0.2em] text-zinc-500 uppercase mt-1">{d.getFullYear()}</div>
        </div>
        <p className="font-mono text-[10px] tracking-[0.25em] text-zinc-400 uppercase lg:mt-3">
          {days === 0 ? "TODAY" : `IN ${days} DAYS`}
        </p>
      </div>

      {/* Details */}
      <div className="lg:col-span-6 min-w-0">
        <p className="font-mono text-[10px] tracking-[0.25em] text-zinc-500 uppercase">0{index + 1} · {race.organiser || "RACE"}</p>
        <h3 className="font-display text-2xl text-white mt-1 leading-tight">{race.title}</h3>
        <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-xs text-zinc-400">
          <span className="inline-flex items-center gap-1.5"><MapPin size={12} className="text-orange-500" />{race.location}</span>
          {race.distances && <span className="inline-flex items-center gap-1.5"><CalendarDays size={12} className="text-orange-500" />{race.distances}</span>}
          {race.entry_fee && <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-500">{race.entry_fee}</span>}
        </div>
        {race.pitch && (
          <blockquote data-testid={`welcome-race-pitch-${race.race_id}`} className="mt-3 flex items-start gap-2 border-l-2 border-orange-600 pl-3 text-sm text-zinc-300 italic">
            <Quote size={12} className="text-orange-500 shrink-0 mt-1" />
            <span>{race.pitch} <span className="not-italic font-mono text-[9px] tracking-[0.2em] text-zinc-500 uppercase ml-1">— Sumit</span></span>
          </blockquote>
        )}
        <div className="mt-4">
          <RaceJoiners race={race} />
        </div>
      </div>

      {/* Actions */}
      <div className="lg:col-span-4 flex flex-wrap lg:flex-col lg:items-end gap-2 lg:justify-center">
        <RaceJoinButton race={race} onChange={onChange} />
        <RacePlanButton race={race} onChange={onChange} />
        {race.registration_url && (
          <a
            href={race.registration_url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`welcome-race-register-${race.race_id}`}
            className="inline-flex items-center gap-1.5 border border-zinc-700 text-zinc-300 hover:text-white hover:border-zinc-500 font-display tracking-wider text-sm px-4 py-2.5 transition"
          >
            REGISTER <ExternalLink size={13} />
          </a>
        )}
      </div>
    </article>
  );
}
